import type { FishIQSpeciesSeed } from '../types';
import { canonicalSpeciesKey, getCanonicalFishIQMetadata } from './fishIQCanonicalSpecies';
import { FISHIQ_WATERBODY_PROFILES } from './fishIQWaterbodyProfiles';
import { FISHIQ_ESTABLISHED_REGIONS } from './fishIQEstablishedRegions';

export type FishIQRegionCoverageRow = {
  id: string;
  label: string;
  speciesCount: number;
  dedicatedSpeciesPack: boolean;
  status: 'covered' | 'thin' | 'empty';
};

export type FishIQDataHealthReport = {
  totalSpecies: number;
  duplicateGroups: number;
  missingScientificName: number;
  missingWaterRealm: number;
  missingHabitat: number;
  missingRegion: number;
  missingSource: number;
  lowCompleteness: number;
  waterbodyProfiles: number;
  unresolvedWaterbodySpeciesIds: string[];
  duplicateExamples: Array<{ key: string; ids: string[] }>;
  establishedRegions: number;
  coveredRegions: number;
  thinRegions: number;
  emptyRegions: number;
  dedicatedSpeciesPacks: number;
  priorityGrowthRegions: Array<{ id: string; speciesCount: number; status: 'covered' | 'thin' | 'empty' }>;
  regionCoverage: FishIQRegionCoverageRow[];
};

export function buildFishIQDataHealthReport(speciesList: FishIQSpeciesSeed[]): FishIQDataHealthReport {
  const groups = new Map<string, string[]>();
  const knownIds = new Set<string>();
  let missingScientificName = 0;
  let missingWaterRealm = 0;
  let missingHabitat = 0;
  let missingRegion = 0;
  let missingSource = 0;
  let lowCompleteness = 0;

  for (const species of speciesList || []) {
    const metadata = getCanonicalFishIQMetadata(species);
    const key = canonicalSpeciesKey(species);
    groups.set(key, [...(groups.get(key) || []), species.id]);
    knownIds.add(String(species.id || '').toLowerCase());
    knownIds.add(String(metadata.canonicalId || '').toLowerCase());
    metadata.legacyIds.forEach((id) => knownIds.add(String(id).toLowerCase()));
    if (!metadata.dataQuality.hasScientificName) missingScientificName += 1;
    if (!metadata.dataQuality.hasWaterRealm) missingWaterRealm += 1;
    if (!metadata.dataQuality.hasHabitat) missingHabitat += 1;
    if (!metadata.dataQuality.hasRegion) missingRegion += 1;
    if (!metadata.dataQuality.hasSource) missingSource += 1;
    if (metadata.dataQuality.completeness < 60) lowCompleteness += 1;
  }

  const duplicateExamples = [...groups.entries()]
    .filter(([, ids]) => ids.length > 1)
    .slice(0, 20)
    .map(([key, ids]) => ({ key, ids }));

  const profileIds = FISHIQ_WATERBODY_PROFILES.flatMap((profile) => [
    ...(profile.likelySpeciesIds || []),
    ...(profile.seasonalSpeciesIds || []),
    ...(profile.stockedSpeciesIds || []),
    ...(profile.excludedSpeciesIds || []),
  ]);
  const unresolvedWaterbodySpeciesIds = [...new Set(profileIds.map((id) => id.toLowerCase()))]
    .filter((id) => !knownIds.has(id))
    .sort();

  const regionCoverage: FishIQRegionCoverageRow[] = FISHIQ_ESTABLISHED_REGIONS.map((region) => {
    const speciesCount = (speciesList || []).filter((species: any) =>
      (species?.regions || []).some((value: string) => String(value).toLowerCase() === region.id),
    ).length;
    return {
      id: region.id,
      label: region.label,
      speciesCount,
      dedicatedSpeciesPack: region.dedicatedSpeciesPack,
      status: (
        speciesCount >= 8
          ? 'covered'
          : speciesCount > 0
            ? 'thin'
            : 'empty'
      ) as FishIQRegionCoverageRow['status'],
    };
  }).sort((a, b) => a.speciesCount - b.speciesCount || a.label.localeCompare(b.label));

  return {
    totalSpecies: speciesList.length,
    duplicateGroups: duplicateExamples.length,
    missingScientificName,
    missingWaterRealm,
    missingHabitat,
    missingRegion,
    missingSource,
    lowCompleteness,
    waterbodyProfiles: FISHIQ_WATERBODY_PROFILES.length,
    unresolvedWaterbodySpeciesIds,
    duplicateExamples,
    establishedRegions: regionCoverage.length,
    coveredRegions: regionCoverage.filter((row) => row.status === 'covered').length,
    thinRegions: regionCoverage.filter((row) => row.status === 'thin').length,
    emptyRegions: regionCoverage.filter((row) => row.status === 'empty').length,
    dedicatedSpeciesPacks: FISHIQ_ESTABLISHED_REGIONS.filter((region) => region.dedicatedSpeciesPack).length,
    priorityGrowthRegions: regionCoverage
      .filter((row) => ['philippines', 'india_inland', 'india_coastal'].includes(row.id))
      .map((row) => ({ id: row.id, speciesCount: row.speciesCount, status: row.status })),
    regionCoverage,
  };
}
